import React, { Dispatch, SetStateAction } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Chip, Grid, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { nameHelperText } from '../../utils/constants/text-field';
import { BasicInfoForm } from '../../utils/models/basic-info';


const dummyTags = [
    { label: "Centurion", meta_label: "" },
    { label: "hd", meta_label: "" },
    { label: "himanshu", meta_label: "" },
]

interface BasicInfoModalProps {
    disableName: boolean,
    open: boolean,
    basicInfo: BasicInfoForm,
    setOpen: Dispatch<SetStateAction<boolean>>,
    handleModalSubmit: (formData: BasicInfoForm) => void,
}

const BasicInfoModal: React.FC<BasicInfoModalProps> = ({
    disableName,
    open,
    basicInfo,
    setOpen,
    handleModalSubmit
}) => {

    const initialBasicInfoValues = {
        name: basicInfo.name,
        description: basicInfo.description,
        tags: basicInfo.tags,
    }

    const { control, handleSubmit, reset, formState: { errors }, getValues, trigger } = useForm<BasicInfoForm>({
        defaultValues: initialBasicInfoValues
    });

    const handleClose = async () => {
        const isValid = await trigger("name")
        const name = getValues("name")
        console.log(!isValid || !basicInfo.name || !name || !!errors.name)

        if (!isValid || !basicInfo.name || !name || !!errors.name) {
            return;
        }
        setOpen(false);
    };

    const handleBasicInfoSubmit: SubmitHandler<BasicInfoForm> = (formData: BasicInfoForm) => {
        // console.log("basic info: ", formData);
        handleModalSubmit({ ...formData, description: formData.description.trim() });
        setOpen(false);
        reset();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            component='form'
            onSubmit={handleSubmit(handleBasicInfoSubmit)}
            aria-labelledby="basic-info-dialog-title"
            sx={{
                '& .MuiDialog-paperWidthSm': {
                    width: '650px',
                    maxWidth: '650px',
                    height: '500px'
                },
            }}
        >
            <DialogTitle id="customized-dialog-title"
                sx={{
                    m: 0,
                    pt: 1.5,
                    pb: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant='h6' component='p'>Basic Info</Typography>
                    <Typography variant='subtitle2' component='p'>Edit Name and Description</Typography>
                </Box>

                <IconButton
                    aria-label="close"
                    type='button'
                    onClick={handleClose}
                    sx={{ width: 45, height: 45 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: 'Name is required',
                                    minLength: { value: 6, message: 'Name must be at least 6 characters' },
                                    maxLength: { value: 30, message: 'Name must be at most 30 characters' },
                                    pattern: {
                                        value: /^[a-zA-Z]+[a-zA-Z0-9-]*[a-zA-Z0-9]$/,
                                        message: 'Name must start with a letter and can contain letters, numbers, and hypens',
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Name"
                                        variant="outlined"
                                        error={!!errors.name}
                                        helperText={errors.name ? errors.name?.message : nameHelperText}
                                        autoComplete='off'
                                        InputProps={{
                                            readOnly: disableName,
                                        }}
                                        fullWidth
                                        autoFocus
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    maxLength: { value: 100, message: 'Description must be at most 100 characters' },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Description"
                                        placeholder='Description (Optional)'
                                        variant="outlined"
                                        error={!!errors.description}
                                        helperText={errors.description && `${errors.description?.message}. ${field.value.length}/100`}
                                        multiline
                                        rows={3}
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name='tags'
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        multiple
                                        freeSolo
                                        filterSelectedOptions
                                        id="tags-filled"
                                        value={field.value}
                                        onChange={(e, newValue) => field.onChange(newValue)}
                                        options={dummyTags.map((option) => option.label)}
                                        renderTags={(value: readonly string[], getTagProps) =>
                                            value.map((option: string, index: number) => {
                                                const { key, ...tagProps } = getTagProps({ index });
                                                return (
                                                    <Chip variant="filled" label={option} key={key} {...tagProps} />
                                                );
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label="Tags"
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button type='button' variant="outlined" onClick={handleClose} sx={{ width: 120 }}>
                    Cancel
                </Button>

                <Button type='submit' variant="contained" sx={{ width: 120 }} disableElevation>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BasicInfoModal